import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

// Referans şirketleri - Güncelleniyor
const REFERENCES: { id: string; name: string; logo: string; sector: string }[] = [];

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.1, 0.25, 1] as const,
    },
  },
};

export function ProjectsPage() {
  return (
    <>
      <title>Referanslar | Turco Sandalye</title>

      {/* Hero Section */}
      <section className="bg-charcoal pt-36 pb-16 md:pt-40 md:pb-20 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal via-charcoal/80 to-charcoal" />
        </div>

        <div className="container mx-auto px-6 relative z-10 text-center max-w-3xl">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-gold text-xs md:text-sm font-semibold uppercase tracking-widest mb-4 block"
          >
            Güvenilir İş Ortaklıkları
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-display text-4xl md:text-5xl lg:text-6xl mb-6"
          >
            Referanslarımız
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-lg md:text-xl text-gray-400 font-light leading-relaxed"
          >
            Türkiye'nin ve dünyanın önde gelen markalarıyla çalışmaktan gurur duyuyoruz.
          </motion.p>
        </div>
      </section>

      {/* References Grid */}
      <section className="bg-white py-16 md:py-24">
        <div className="container mx-auto px-6 lg:px-12">
          {REFERENCES.length > 0 ? (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-50px' }}
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 lg:gap-8"
            >
              {REFERENCES.map((ref) => (
                <motion.div
                  key={ref.id}
                  variants={itemVariants}
                  className="group relative bg-gray-50 hover:bg-white border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-300 aspect-[3/2] flex items-center justify-center p-6 md:p-8"
                >
                  {/* Logo */}
                  <img
                    src={ref.logo}
                    alt={ref.name}
                    className="max-w-full max-h-full object-contain filter grayscale group-hover:grayscale-0 opacity-60 group-hover:opacity-100 transition-all duration-300"
                    style={{ maxHeight: '60px' }}
                    loading="lazy"
                  />

                  {/* Hover overlay with sector info */}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-charcoal/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-4 pt-8">
                    <p className="text-white text-xs md:text-sm font-medium text-center">
                      {ref.name}
                    </p>
                    <p className="text-gray-400 text-[10px] md:text-xs text-center mt-1">
                      {ref.sector}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center py-16"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-6">
                <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="font-display text-2xl md:text-3xl text-charcoal mb-4">
                Sizler İçin Güncelleniyor
              </h3>
              <p className="text-gray-500 max-w-md mx-auto">
                Referanslarımız bölümü yenileniyor. En kısa sürede güncel iş ortaklıklarımızı burada paylaşacağız.
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-stone-50 py-16 md:py-20 border-y border-gray-100">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className="font-display text-4xl md:text-5xl text-charcoal block mb-2">500+</span>
              <span className="text-gray-500 text-sm md:text-base">Tamamlanan Proje</span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <span className="font-display text-4xl md:text-5xl text-charcoal block mb-2">40+</span>
              <span className="text-gray-500 text-sm md:text-base">Yıllık Deneyim</span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <span className="font-display text-4xl md:text-5xl text-charcoal block mb-2">15+</span>
              <span className="text-gray-500 text-sm md:text-base">Ülkede Hizmet</span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <span className="font-display text-4xl md:text-5xl text-charcoal block mb-2">%98</span>
              <span className="text-gray-500 text-sm md:text-base">Müşteri Memnuniyeti</span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-charcoal text-white py-20 md:py-24 text-center border-b border-white/10">
        <div className="container mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-display text-3xl md:text-4xl mb-6"
          >
            Referanslarımız Arasına Katılın
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-gray-400 mb-10 max-w-xl mx-auto"
          >
            Projeniz için profesyonel çözüm ortağınız olmaya hazırız.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Link
              to="/contact"
              className="inline-block bg-white text-charcoal px-10 py-4 text-sm font-bold uppercase tracking-widest hover:bg-gold transition-colors"
            >
              İletişime Geçin
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
